import React, { useCallback, useState } from 'react';

import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  TextInput,
  minLength,
  maxLength,
  required,
} from 'react-admin';
import { QRCodeSVG } from 'qrcode.react';

const QR_CODE_SIZE = 176;
const QR_CODE_PADDING = 16;
const CODE_BOX_BORDER = 1;
const CODE_BOX_SIZE = QR_CODE_SIZE + (QR_CODE_PADDING + CODE_BOX_BORDER) * 2;
const SETUP_KEY_GROUP_SIZE = 4;
const SETUP_KEY_GROUPS_PER_ROW = 4;
const SETUP_KEY_FONT_SIZE = 19;

const setupKeyRows = (key: string) => {
  const groups = key.match(new RegExp(`.{1,${SETUP_KEY_GROUP_SIZE}}`, 'g')) ?? [];
  const rows: string[] = [];
  for (let index = 0; index < groups.length; index += SETUP_KEY_GROUPS_PER_ROW) {
    rows.push(groups.slice(index, index + SETUP_KEY_GROUPS_PER_ROW).join(' '));
  }
  return rows.join('\n');
};

type VerifyFormFields = {
  code?: string;
}

const MfaEnrollmentVerify = ({
  loading,
  secret,
  enrollmentUri,
  confirmLabel,
  centered = false,
  onConfirm,
  onCancel,
}: {
  loading: boolean;
  secret: string;
  enrollmentUri: string;
  confirmLabel: string;
  centered?: boolean;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}) => {
  const [showSetupKey, setShowSetupKey] = useState(false);

  const toggleSetupKey = useCallback(() => {
    setShowSetupKey((shown) => !shown);
  }, [setShowSetupKey]);

  const submit = useCallback((data: VerifyFormFields) => {
    if (data.code) onConfirm(data.code);
  }, [onConfirm]);

  const blockAlignment = centered ? 'auto' : 0;

  return (
    <Box textAlign={centered ? 'center' : undefined}>
      <Typography variant="h6" gutterBottom>
        Pair your authenticator app
      </Typography>
      <Box marginTop={2} marginBottom={3} maxWidth={360} marginX={blockAlignment}>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {showSetupKey
            ? 'Enter this setup key in your authenticator app.'
            : 'Scan this code with your authenticator app.'}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            padding: `${QR_CODE_PADDING}px`,
            border: showSetupKey ? 'none' : undefined,
            width: 'fit-content',
            maxWidth: '100%',
            minHeight: CODE_BOX_SIZE,
            boxSizing: 'border-box',
            marginX: blockAlignment,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {showSetupKey ? (
            <Typography
              variant="body1"
              fontFamily="monospace"
              sx={{
                fontSize: SETUP_KEY_FONT_SIZE,
                textAlign: 'center',
                letterSpacing: 0.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {setupKeyRows(secret)}
            </Typography>
          ) : (
            <QRCodeSVG value={enrollmentUri} size={QR_CODE_SIZE} />
          )}
        </Paper>
        <Box>
          <Button
            variant="text"
            size="small"
            onClick={toggleSetupKey}
            sx={{ paddingLeft: centered ? undefined : 0 }}
          >
            {showSetupKey ? 'Show QR code' : "Can't scan it? Set up manually"}
          </Button>
        </Box>
      </Box>
      <Form onSubmit={submit}>
        <Typography variant="body1" color="text.secondary">
          Enter the 6 digit code your app shows to finish the setup.
        </Typography>
        <Box maxWidth={centered ? undefined : 220} marginX={blockAlignment}>
          <TextInput
            source="code"
            label="Verification code"
            disabled={loading}
            validate={[required(), minLength(6), maxLength(6)]}
            fullWidth
          />
        </Box>
        <Stack direction="row" spacing={2} justifyContent={centered ? 'center' : undefined}>
          <Button variant="contained" type="submit" disabled={loading}>
            {confirmLabel}
          </Button>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

export default MfaEnrollmentVerify;
